import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import { exercises, ExerciseType } from '../../data/exercises';
import './ExerciseDetail.css';

import BreathingExercise from '../../components/BreathingExercise/BreathingExercise';
import BreathingExerciseSloped from '../../components/BreathingExercise/BreathingExerciseSloped';

const ExerciseDetail: React.FC = () => {
    const { exerciseId } = useParams<{ exerciseId: string }>();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);

    const exercise = exercises.find(e => e.id === exerciseId);

    if (!exercise) {
        return (
            <div className="main-content-container">
                <div className="page-header">Упражнение не найдено</div>
                <div className="exercise-container">
                    <button className="exercise-nav-button" onClick={() => navigate('/materials')}>
                        Вернуться к материалам
                    </button>
                </div>
            </div>
        );
    }

    const handleNext = () => {
        if (currentStep < exercise.steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            setIsCompleted(true);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleFinish = () => {
        navigate('/materials/exercises');
    };

    // For breathing exercise completion
    const handleBreathingComplete = () => {
        setIsCompleted(true);
    };

    const progress = exercise.type === ExerciseType.INTERACTIVE
        ? 100 // Interactive exercises don't track linear progress
        : ((currentStep + 1) / exercise.steps.length) * 100;

    return (
        <div className="main-content-container">
            <SEO
                title={`${exercise.title} | Полезные материалы`}
                description={exercise.shortDescription}
                canonical={`/materials/exercise/${exerciseId}`}
            />

            <div className="exercise-container fade-in">
                <div className="exercise-header">
                    <h1 className="exercise-title">{exercise.title}</h1>
                    <button className="exercise-close-button" onClick={() => navigate('/materials/exercises')}>
                        ✕
                    </button>
                </div>


                {exercise.type === ExerciseType.INTERACTIVE && exercise.breathingConfig ? (
                    <>
                        {(exercise.description || exercise.steps.length > 0) && (
                            <details className="test-info-details">
                                <summary className="test-info-summary">Описание и инструкция</summary>
                                <div className="test-info-content">
                                    {exercise.description && (
                                        <div className="test-description-section">
                                            <h3>Описание</h3>
                                            <p>{exercise.description}</p>
                                        </div>
                                    )}

                                    {exercise.steps.length > 0 && (
                                        <div className="test-instruction-section">
                                            <h3>Инструкция</h3>
                                            <div className="instruction-text">
                                                {exercise.steps.map((step, index) => (
                                                    <div key={index} style={{ marginBottom: '15px' }}>
                                                        <strong>{step.title}</strong>
                                                        <p style={{ margin: '5px 0' }}>{step.description}</p>
                                                        {step.instruction && (
                                                            <div style={{ paddingLeft: '15px', borderLeft: '3px solid #eee', marginTop: '5px', whiteSpace: 'pre-line', color: '#555' }}>
                                                                {step.instruction}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}

                        <div className="exercise-content">
                            {!isCompleted ? (
                                exercise.breathingConfig?.visualType === 'slope' ? (
                                    <BreathingExerciseSloped
                                        config={exercise.breathingConfig}
                                        onComplete={handleBreathingComplete}
                                        onCancel={() => navigate('/materials/exercises')}
                                    />
                                ) : (
                                    <BreathingExercise
                                        config={exercise.breathingConfig}
                                        onComplete={handleBreathingComplete}
                                        onCancel={() => navigate('/materials/exercises')}
                                    />
                                )
                            ) : (
                                <div className="exercise-success-message">
                                    <h2>Упражнение выполнено!</h2>
                                    {exercise.completionMessage ? (
                                        <div className="step-instruction">
                                            {exercise.completionMessage.split('\n').map((line, i) => (
                                                <p key={i} style={{ margin: '0 0 10px 0' }}>{line}</p>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>Вы успешно завершили упражнение.</p>
                                    )}
                                    <div className={`exercise-navigation center`}>
                                        <button className="exercise-nav-button primary" onClick={handleFinish}>
                                            Вернуться к упражнениям
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {(exercise.description || exercise.steps.length > 0) && (
                            <details className="test-info-details">
                                <summary className="test-info-summary">Описание и инструкция</summary>
                                <div className="test-info-content">
                                    {exercise.description && (
                                        <div className="test-description-section">
                                            <h3>Описание</h3>
                                            <p>{exercise.description}</p>
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}

                        {!isCompleted && (
                            <div className="progress-bar-container">
                                <div className="progress-bar" style={{ width: `${progress}%` }} />
                            </div>
                        )}

                        <div className="exercise-content">
                            {!isCompleted ? (
                                <>
                                    <div className="step-counter">
                                        Шаг {currentStep + 1} из {exercise.steps.length}
                                    </div>

                                    <h2 className="step-title">{exercise.steps[currentStep].title}</h2>

                                    <div className="step-description">
                                        {exercise.steps[currentStep].description}
                                    </div>

                                    {exercise.steps[currentStep].instruction && (
                                        <div className="step-instruction">
                                            {exercise.steps[currentStep].instruction}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="exercise-success-message">
                                        <h2>Упражнение выполнено!</h2>

                                        {exercise.completionMessage ? (
                                            <div className="step-instruction">
                                                {exercise.completionMessage.split('\n').map((line, i) => (
                                                    <p key={i} style={{ margin: '0 0 10px 0' }}>{line}</p>
                                                ))}
                                            </div>
                                        ) : (
                                            <p>Вы успешно завершили упражнение.</p>
                                        )}

                                        <p style={{ marginTop: '20px', color: '#666' }}>
                                            Вы можете вернуться к этому упражнению в любое время.
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className={`exercise-navigation ${isCompleted ? 'center' : ''}`}>
                            {!isCompleted ? (
                                <>
                                    <button
                                        className="exercise-nav-button secondary"
                                        onClick={handleBack}
                                        disabled={currentStep === 0}
                                    >
                                        Назад
                                    </button>
                                    <button
                                        className="exercise-nav-button primary"
                                        onClick={handleNext}
                                    >
                                        {currentStep === exercise.steps.length - 1 ? 'Завершить' : 'Далее'}
                                    </button>
                                </>
                            ) : (
                                <button className="exercise-nav-button primary" onClick={handleFinish}>
                                    Вернуться к упражнениям
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ExerciseDetail;
